import { ReactNode, useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion, type MotionProps } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Apple-style scroll reveal primitives.
 * Easing curve: [0.22, 1, 0.36, 1] (quint-out) — the canonical Apple feel.
 */

const APPLE_EASE = [0.22, 1, 0.36, 1] as const;

type RevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  /** distance in px the element travels in from below */
  y?: number;
  /** start scale (1 = no zoom). 0.96 reads as a soft Apple zoom-in */
  scale?: number;
  /** start blur in px (Apple uses subtle 8–12) */
  blur?: number;
  duration?: number;
  /** % of element that must enter the viewport before animating */
  amount?: number;
  once?: boolean;
  as?: "div" | "section" | "header" | "h1" | "h2" | "h3" | "p" | "span" | "li" | "ul";
};

export function Reveal({
  children,
  className,
  delay = 0,
  y = 28,
  scale = 1,
  blur = 8,
  duration = 0.9,
  amount = 0.2,
  once = true,
  as = "div",
}: RevealProps) {
  const reduce = useReducedMotion();
  const Comp = motion[as] as React.ComponentType<MotionProps & { className?: string; children?: ReactNode }>;

  if (reduce) {
    const Static = as as React.ElementType;
    return <Static className={className}>{children}</Static>;
  }

  return (
    <Comp
      className={className}
      initial={{ opacity: 0, y, scale, filter: `blur(${blur}px)` }}
      whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
      viewport={{ once, amount }}
      transition={{ duration, delay, ease: APPLE_EASE }}
    >
      {children}
    </Comp>
  );
}

/**
 * Children stagger — wrap a list/grid container; immediate children animate in
 * with a soft cascade. Children must be motion-friendly nodes (divs, etc.).
 */
export function RevealStagger({
  children,
  className,
  delay = 0,
  stagger = 0.08,
  y = 24,
  blur = 6,
  duration = 0.8,
  amount = 0.15,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  stagger?: number;
  y?: number;
  blur?: number;
  duration?: number;
  amount?: number;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
    >
      {Array.isArray(children)
        ? children.map((child, i) => (
            <motion.div
              key={i}
              variants={{
                hidden: { opacity: 0, y, filter: `blur(${blur}px)` },
                visible: {
                  opacity: 1,
                  y: 0,
                  filter: "blur(0px)",
                  transition: { duration, ease: APPLE_EASE },
                },
              }}
            >
              {child}
            </motion.div>
          ))
        : (
          <motion.div
            variants={{
              hidden: { opacity: 0, y, filter: `blur(${blur}px)` },
              visible: {
                opacity: 1,
                y: 0,
                filter: "blur(0px)",
                transition: { duration, ease: APPLE_EASE },
              },
            }}
          >
            {children}
          </motion.div>
        )}
    </motion.div>
  );
}

/**
 * Subtle parallax — translates content vertically as the section scrolls
 * through the viewport. Range default = -40..40px.
 */
export function Parallax({
  children,
  className,
  offset = 60,
}: {
  children: ReactNode;
  className?: string;
  offset?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);

  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * Scroll-driven scale + opacity for hero/cinematic blocks.
 * Element shrinks slightly and dims as it scrolls past — Apple landing page feel.
 */
export function ScrollCinematic({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
  const opacity = useTransform(scrollYProgress, [0, 0.8, 1], [1, 0.85, 0.5]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);

  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} style={{ scale, opacity, y }} className={cn("will-change-transform", className)}>
      {children}
    </motion.div>
  );
}
